import { Button } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import type { ProjectReference } from 'app/modules/wallforming/components/projects-manager/types/projectReference';

interface LoadProjectDialogProps {
    candidate: ProjectReference;
    hasUnsavedChanges: boolean;
    isLoadPending: boolean;
    isSavePending: boolean;
    onClose: () => void;
    onLoad: () => void;
    onSaveAndLoad: () => void;
}

export const LoadProjectDialog = ({
    candidate,
    hasUnsavedChanges,
    isLoadPending,
    isSavePending,
    onClose,
    onLoad,
    onSaveAndLoad
}: LoadProjectDialogProps) => {
    const { t } = useTranslation();

    return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/25">
            <div
                className="w-[520px] max-w-[92vw] rounded-md border border-border-light bg-background-default shadow-xl"
                onPointerDown={(event) => event.stopPropagation()}
            >
                <div className="border-b border-border-light px-3 py-2 text-sm font-semibold text-text-default">
                    {t('labelLoadProject')}
                </div>

                <div className="space-y-3 p-3 text-sm text-text-default">
                    <div>{t('projectsLoadConfirm', { projectName: candidate.name })}</div>

                    {hasUnsavedChanges && (
                        <div className="rounded-sm border border-border-warning bg-background-warning-light px-3 py-2 text-xs text-text-default mb-6">
                            <span className="normal-case text-text-warning text-sm-bold" style={{ marginLeft: 0 }}>
                                <Icon
                                    className="action-menu-entry-decoration action-menu-entry-decoration-icon mr-0.5"
                                    icon="warning"
                                />
                                {t('warning')}
                            </span>
                            <br />
                            {t('projectsUnsavedBeforeLoad')}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <Button
                            disabled={isLoadPending || isSavePending}
                            size={sizes.button.small}
                            variant={variants.button.secondary}
                            onClick={onClose}
                        >
                            {t('cancel')}
                        </Button>
                        {hasUnsavedChanges && (
                            <Button
                                disabled={isLoadPending || isSavePending}
                                loading={isSavePending}
                                size={sizes.button.small}
                                variant={variants.button.secondary}
                                onClick={onSaveAndLoad}
                            >
                                {t('projectsSaveAndLoad')}
                            </Button>
                        )}
                        <Button
                            disabled={isSavePending}
                            loading={isLoadPending}
                            size={sizes.button.small}
                            onClick={onLoad}
                        >
                            {hasUnsavedChanges ? t('projectsLoadWithoutSaving') : t('load')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
